import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import _isFunction from 'lodash/isFunction';
import useModalState, { UseModalState } from './modal-hook';

interface Props {
	isOpen: boolean;
	bodyClassName?: string;
	className?: string;
	onClickOutside?: () => void;
}

interface CustomModalComponent<Props> extends React.FC<Props> {
	useModalState: UseModalState;
}

const CustomModal: CustomModalComponent<Props> = ({ isOpen, bodyClassName, className, children, onClickOutside }) => {
	const [shouldRender, setShouldRender] = useState(isOpen);
	const contentRef = useRef(null);

	useEffect(() => {
		if (isOpen) {
			setShouldRender(isOpen);
		}
	}, [isOpen]);

	const onAnimationEnd = event => {
		if (!isOpen && event.target.hasAttribute('data-background-element')) {
			setShouldRender(isOpen);
		}
	};

	useEffect(() => {
		if (isOpen && _isFunction(onClickOutside)) {
			function handleClickOutside(event) {
				if (contentRef.current && !contentRef.current.contains(event.target)) {
					onClickOutside();
				}
			}
			document.addEventListener('mousedown', handleClickOutside);
			return () => {
				document.removeEventListener('mousedown', handleClickOutside);
			};
		}
	}, [contentRef, onClickOutside, isOpen]);

	return (
		shouldRender && (
			<div
				className={classNames('custom-modal', className, isOpen ? 'custom-mount' : 'custom-unmount')}
				onAnimationEnd={onAnimationEnd}
				data-background-element
			>
				<div ref={contentRef} className={classNames('modal-box', bodyClassName)}>
					<div className="animate-content">{children}</div>
				</div>
			</div>
		)
	);
};

CustomModal.useModalState = useModalState;

export default CustomModal;
